import React, {useCallback, useEffect, useState} from 'react';
import {ShieldCheck, LockKeyhole, RefreshCcw, CheckCircle2, Eye, EyeOff, ArrowRight, AlertCircle, KeyRound, Info, ShieldAlert} from 'lucide-react';
import {Link} from 'react-router-dom';

const API=(process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const authHeaders=()=>({Authorization:`Bearer ${localStorage.getItem('token') || ''}`, 'Content-Type':'application/json'});
async function readJson(response) {
  const data=await response.json().catch(()=>({}));
  if(!response.ok){
    const error=new Error(data.msg || (response.status===503 ? 'Layanan sedang mengalami gangguan. Coba lagi sebentar.' : 'Permintaan belum berhasil.'));
    error.status=response.status;
    throw error;
  }
  return data;
}

function PasswordField({id,label,value,onChange,show,onToggle,autoComplete,disabled,help}) {
  return <div className="space-y-2">
    <label htmlFor={id} className="block text-sm font-semibold text-slate-800">{label}</label>
    <div className="relative">
      <input id={id} name={id} aria-label={label} type={show?'text':'password'}
        autoComplete={autoComplete} value={value} onChange={e=>onChange(e.target.value)}
        disabled={disabled} required minLength={id==='current-password'?1:12}
        className="block w-full h-12 rounded-xl border border-slate-300 bg-white px-4 pr-14 text-base text-slate-900 outline-none transition focus:border-blue-600 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100 disabled:text-slate-500"
        placeholder={id==='current-password'?'Masukkan password saat ini':'Minimal 12 karakter'} />
      <button type="button" onClick={onToggle} disabled={disabled}
        aria-label={`${show?'Sembunyikan':'Tampilkan'} ${label.toLowerCase()}`}
        aria-pressed={show}
        className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-9 w-10 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:opacity-40">
        {show?<EyeOff size={19}/>:<Eye size={19}/>}</button>
    </div>
    {help&&<p className="text-xs text-slate-500">{help}</p>}
  </div>;
}

export default function AccountCenter({role='guru'}) {
  const [me,setMe]=useState(null);
  const [meError,setMeError]=useState('');
  const [loadingMe,setLoadingMe]=useState(true);
  const [current,setCurrent]=useState('');
  const [next,setNext]=useState('');
  const [confirm,setConfirm]=useState('');
  const [visible,setVisible]=useState({current:false,next:false,confirm:false});
  const [busy,setBusy]=useState(false);
  const [notice,setNotice]=useState(null);
  const [requests,setRequests]=useState([]);
  const [requestsError,setRequestsError]=useState('');
  const [requestBusy,setRequestBusy]=useState(false);
  const [code,setCode]=useState('');
  const isReviewer=['admin','satlak'].includes(role);
  const isDefault=me?.password_is_default===true;
  const tooShort=next.length>0 && next.length<12;
  const mismatch=confirm.length>0 && next!==confirm;
  const allReady=!!current && next.length>=12 && next===confirm && !isDefault && !busy && !loadingMe && !meError;

  const load=useCallback(async()=>{
    setLoadingMe(true);setMeError('');
    try {
      const res=await fetch(`${API}/api/account/me`,{headers:authHeaders(),cache:'no-store'});
      setMe(await readJson(res));
    }catch(err){setMe(null);setMeError(err.message);}
    finally{setLoadingMe(false);}
  },[]);
  const loadRequests=useCallback(async()=>{
    setRequestsError('');
    try {
      const res=await fetch(`${API}/api/account/requests`,{headers:authHeaders(),cache:'no-store'});
      const data=await readJson(res);
      setRequests(Array.isArray(data)?data:[]);
    }catch(err){setRequestsError(err.message);}
  },[]);
  useEffect(()=>{load();if(isReviewer)loadRequests();},[load,loadRequests,isReviewer]);

  async function change(event){
    event.preventDefault();setNotice(null);
    if(isDefault){setNotice({type:'error',text:'Password awal masih sama dengan NIP. Gunakan Lupa Password dan verifikasi identitas oleh Satlak/Admin.'});return;}
    if(!current){setNotice({type:'error',text:'Masukkan password yang sedang digunakan.'});return;}
    if(next.length<12){setNotice({type:'error',text:'Password baru harus minimal 12 karakter.'});return;}
    if(next!==confirm){setNotice({type:'error',text:'Ulangi password baru dengan benar.'});return;}
    setBusy(true);
    try {
      const res=await fetch(`${API}/api/account/change-password`,{
        method:'POST',headers:authHeaders(),body:JSON.stringify({old_password:current,new_password:next})
      });
      const data=await readJson(res);
      setCurrent('');setNext('');setConfirm('');setVisible({current:false,next:false,confirm:false});
      setNotice({type:'success',text:data.msg || 'Password berhasil diubah. Keluar dari akun dan masuk kembali menggunakan password baru.'});
      await load();
    }catch(err){
      setNotice({type:'error',text:err.message+(err.status===429?' Tunggu sebelum mencoba kembali.':'')});
    }finally{setBusy(false);}
  }
  async function decide(id,action){
    if(action==='approve'&&!window.confirm('Identitas guru telah diverifikasi sesuai prosedur? Kode diberikan secara pribadi, bukan lewat grup.'))return;
    setRequestBusy(true);setCode('');setRequestsError('');
    try {
      const res=await fetch(`${API}/api/account/requests/${id}/${action}`,{
        method:'POST',headers:authHeaders(),body:JSON.stringify({identity_verified:action==='approve'})
      });
      const data=await readJson(res);
      if(data.code)setCode(data.code);
      setRequestsError(data.msg || 'Permintaan berhasil diproses.');
      await loadRequests();
    }catch(err){setRequestsError(err.message);}finally{setRequestBusy(false);}
  }

  return <main className="mx-auto w-full max-w-5xl space-y-6 px-4 py-5 text-slate-900 sm:px-6 md:py-8">
    <header className="overflow-hidden rounded-2xl bg-gradient-to-r from-blue-950 to-blue-700 px-5 py-6 text-white shadow-sm sm:px-8 sm:py-8">
      <div className="flex flex-wrap items-center gap-3">
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15"><ShieldCheck size={27}/></span>
        <div><p className="text-xs font-bold uppercase tracking-widest text-blue-100">SIPAK · Akun Saya</p>
          <h1 className="mt-1 text-2xl font-bold sm:text-3xl">Keamanan Akun</h1></div>
      </div>
      <p className="mt-4 max-w-2xl text-sm leading-6 text-blue-100 sm:text-base">Kelola password pribadi agar akses SIPAK dan dokumen tetap aman. Username tetap menggunakan NIP.</p>
    </header>

    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <section className="min-w-0 space-y-5 lg:col-span-2">
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-5 py-5 sm:px-7">
            <div className="flex items-center gap-3"><span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-700"><LockKeyhole size={21}/></span>
              <div><h2 className="text-xl font-bold">Ganti Password</h2><p className="text-sm text-slate-500">Khusus akun yang sudah memiliki password pribadi.</p></div>
            </div>
          </div>
          <div className="space-y-5 px-5 py-6 sm:px-7">
            {loadingMe&&<p role="status" className="rounded-xl bg-slate-50 p-4 text-sm text-slate-600">Memeriksa keamanan akun...</p>}
            {meError&&<div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              <p className="font-bold">Status akun belum dapat diperiksa.</p><p className="mt-1">{meError}</p>
              <button type="button" onClick={load} className="mt-2 inline-flex items-center gap-2 font-semibold underline"><RefreshCcw size={15}/> Coba lagi</button>
            </div>}
            {isDefault&&<div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-950">
              <div className="flex items-start gap-3"><ShieldAlert size={22} className="mt-0.5 shrink-0 text-amber-700"/>
                <div className="space-y-2"><h3 className="font-bold">Akun masih menggunakan password awal (NIP)</h3>
                  <p className="text-sm leading-6">Demi keamanan, password awal tidak dapat diganti langsung dari formulir ini. Identitas guru harus diverifikasi terlebih dahulu oleh Satlak/Admin melalui menu Lupa Password.</p>
                  {role==='guru'?<Link to="/lupa-password" className="inline-flex items-center gap-2 rounded-lg bg-amber-900 px-4 py-2.5 text-sm font-bold text-white hover:bg-amber-950">Aktivasi password pribadi <ArrowRight size={16}/></Link>:<p className="text-sm font-semibold">Hubungi pengelola akun untuk proses verifikasi identitas.</p>}
                </div></div>
            </div>}
            {!isDefault&&<form onSubmit={change} noValidate className="space-y-5">
              <PasswordField id="current-password" label="Password saat ini" value={current} onChange={setCurrent}
                show={visible.current} onToggle={()=>setVisible(s=>({...s,current:!s.current}))} disabled={busy||loadingMe||!!meError} autoComplete="current-password"/>
              <div className="h-px bg-slate-100"/>
              <PasswordField id="new-password" label="Password baru" value={next} onChange={setNext}
                show={visible.next} onToggle={()=>setVisible(s=>({...s,next:!s.next}))} disabled={busy||loadingMe||!!meError} autoComplete="new-password"/>
              <PasswordField id="confirm-password" label="Ulangi password baru" value={confirm} onChange={setConfirm}
                show={visible.confirm} onToggle={()=>setVisible(s=>({...s,confirm:!s.confirm}))} disabled={busy||loadingMe||!!meError} autoComplete="new-password"/>
              <div className="flex flex-wrap gap-3 text-xs font-medium">
                <span className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 ${next.length>=12?'bg-emerald-50 text-emerald-800':'bg-slate-100 text-slate-600'}`}>
                  <CheckCircle2 size={15}/> Minimal 12 karakter</span>
                <span className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 ${confirm&&next===confirm?'bg-emerald-50 text-emerald-800':'bg-slate-100 text-slate-600'}`}>
                  <CheckCircle2 size={15}/> Konfirmasi sesuai</span>
              </div>
              {(tooShort||mismatch)&&<p role="alert" className="text-sm font-medium text-amber-800">{tooShort?'Password baru belum mencapai 12 karakter.':'Konfirmasi password belum sama.'}</p>}
              {notice&&<div role={notice.type==='error'?'alert':'status'} aria-live="polite"
                className={`flex items-start gap-3 rounded-xl border p-4 text-sm leading-6 ${notice.type==='success'?'border-emerald-200 bg-emerald-50 text-emerald-900':'border-red-200 bg-red-50 text-red-900'}`}>
                {notice.type==='success'?<CheckCircle2 className="mt-0.5 shrink-0" size={20}/>:<AlertCircle className="mt-0.5 shrink-0" size={20}/>}
                <span>{notice.text}</span>
              </div>}
              <div className="flex flex-col gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-slate-500">Lupa password saat ini? <Link className="font-bold text-blue-700 underline" to="/lupa-password">Buka pemulihan akun</Link></p>
                <button disabled={!allReady} type="submit" className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-blue-700 px-5 py-3 font-bold text-white shadow-sm transition hover:bg-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500 sm:w-auto">
                  {busy?<RefreshCcw size={18} className="animate-spin"/>:<KeyRound size={18}/>} {busy?'Menyimpan...':'Simpan password baru'}
                </button>
              </div>
            </form>}
            {isDefault&&<p className="text-sm text-slate-500">Sesudah verifikasi dan membuat password pribadi, kembali ke halaman ini untuk mengganti password kapan saja.</p>}
          </div>
        </div>
      </section>

      <aside className="space-y-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="flex items-center gap-2 font-bold"><Info size={19} className="text-blue-700"/> Tips keamanan</div>
          <div className="mt-4 space-y-3 text-sm leading-6 text-slate-600">
            <p>Gunakan password panjang yang tidak sama dengan NIP dan tidak dipakai di layanan lain.</p>
            <p>Jangan kirim password atau kode pemulihan melalui grup WhatsApp.</p>
            <p>Jika muncul pesan layanan belum tersedia, tunggu sebentar. Jangan mencoba berkali-kali secara cepat.</p>
          </div>
        </div>
        <div className="rounded-2xl border border-blue-100 bg-blue-50 p-5 text-sm leading-6 text-blue-900 sm:p-6">
          <p className="font-bold">Butuh bantuan?</p>
          <p className="mt-1">Hubungi Satlak Pendidikan Kecamatan atau Admin Sudin untuk verifikasi identitas dan pemulihan akses.</p>
        </div>
      </aside>
    </div>

    {isReviewer&&<section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2"><ShieldCheck size={23} className="text-blue-700"/><h2 className="text-xl font-bold">Permohonan Lupa Password</h2></div>
        <button type="button" onClick={loadRequests} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-blue-700 hover:bg-slate-50"><RefreshCcw size={16}/> Muat ulang</button>
      </div>
      <p className="mt-3 text-sm leading-6 text-slate-600">Verifikasi identitas guru mengikuti prosedur resmi sebelum menyetujui. Satlak hanya mengelola guru di kecamatannya.</p>
      {requestsError&&<div role="status" className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">{requestsError}</div>}
      {code&&<div role="status" className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-4">
        <p className="font-bold text-amber-950">Kode pemulihan — hanya tampil sekali</p>
        <p className="my-2 break-all font-mono text-2xl font-bold tracking-wider">{code}</p>
        <p className="text-sm text-amber-900">Berlaku 30 menit. Berikan secara privat hanya kepada guru yang telah diverifikasi.</p>
      </div>}
      <div className="mt-5 space-y-3">
        {!requests.length?<p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">Belum ada permohonan yang menunggu keputusan.</p>:requests.map(r=><div key={r.id} className="flex flex-col justify-between gap-3 rounded-xl border border-slate-200 p-4 sm:flex-row sm:items-center">
          <div className="min-w-0"><p className="font-bold text-slate-900">{r.full_name}</p>
            <p className="mt-1 break-words text-sm text-slate-500">NIP: {r.username} · {r.kecamatan || 'Wilayah belum ditentukan'}</p></div>
          <div className="flex gap-2"><button type="button" disabled={requestBusy} onClick={()=>decide(r.id,'approve')} className="rounded-lg bg-blue-700 px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50">Setujui</button>
            <button type="button" disabled={requestBusy} onClick={()=>decide(r.id,'reject')} className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold disabled:opacity-50">Tolak</button></div>
        </div>)}
      </div>
    </section>}
  </main>;
}
